/**
 * The comment view an app can use (RFC 0324 3b, S2).
 *
 * RFC 0325's road, taken a second time: one framework-free custom element per
 * shape, drawn the same in the window and in a sandboxed frame. This file is
 * the row, one comment with its writer's face; the thread, which is rows plus
 * the reply box and what the exchange is waiting for; and the chip, a count
 * on a passage or a shape.
 *
 * SELF-CONTAINED ON PURPOSE. `installCommentElements` is stringified into a
 * sandboxed frame the way `installTaskElements` is, so nothing inside it may
 * reach a module-level name. Everything it needs is defined inside it.
 */

/** One comment, as the row draws it. */
export interface CommentElementComment {
  id: string
  body: string
  createdAt: number
  mentions?: Array<{ at: number; len: number }>
}

/** Who wrote it: what `projectAuthor` returns. */
export interface CommentElementAuthor {
  label: string
  kind: 'person' | 'agent'
  initials: string
  tint: string
  avatarUrl?: string
}

export interface CommentRowElement extends HTMLElement {
  comment?: CommentElementComment
  author?: CommentElementAuthor
  /** "Now", so a list of rows ages together instead of each on its own clock. */
  now?: number
  /** Quiet actions under the body: Reply, Resolve, Delete. The surface
   *  decides which apply; the row only draws them. */
  actions?: CommentElementAction[]
  /** A short mark beside the time, from the app (RFC 0324 3h). */
  badge?: CommentElementBadge | null
}

/** "outdated", a commit, "on an old version": the app's word on a comment. */
export interface CommentElementBadge {
  text: string
  tone?: 'muted' | 'warn'
  /** Said on hover: which commit an "outdated" comment was written on. */
  title?: string
}

/** Someone a comment can name (RFC 0292), as the port offers them. */
export interface CommentElementCandidate {
  kind: string
  id: string
  label: string
}

/** A name written into a comment: where its "@label" sits in the body. */
export interface CommentElementMention {
  kind: string
  id: string
  label: string
  at: number
  len: number
}

export interface CommentElementAction {
  label: string
  /** Ask first: the first click shows this, the second runs. For an action
   *  with no undo. */
  confirm?: string
  run: () => void
}

/** One exchange as a port hands it over: comments with writers resolved. */
export interface CommentElementThread {
  root: CommentElementComment & {
    authorInfo: CommentElementAuthor
    resolved?: boolean
  }
  replies: Array<CommentElementComment & { authorInfo: CommentElementAuthor }>
}

/**
 * What a thread element is handed, host or frame (RFC 0324 3b). A sandboxed
 * frame cannot read the project, so everything the element shows comes
 * through here. The optional parts draw nothing when absent.
 */
export interface CommentPort {
  threads(target: Record<string, string>): Promise<CommentElementThread[]>
  reply(
    parentId: string,
    body: string,
    mentions?: CommentElementMention[],
  ): Promise<unknown>
  resolve(id: string, resolved: boolean): Promise<unknown>
  /** 3h: start a thread on the target. Needed for `compose`. */
  create?(
    target: Record<string, string>,
    body: string,
    mentions?: CommentElementMention[],
  ): Promise<unknown>
  /** 3h: who a comment can name. Needed for `mentions`. */
  candidates?(prefix: string): Promise<CommentElementCandidate[]>
  /** 3h: delete a comment. Needed for the `delete` action. */
  remove?(id: string): Promise<unknown>
  /** 3c: who a reply would reach, before it is sent. */
  reader?(
    target: Record<string, string>,
    replyTo: string,
  ): Promise<{ text: string; tone: 'normal' | 'warn' } | null>
  /** 3d: what the thread is waiting for. */
  pending?(
    rootId: string,
  ): Promise<{ state: 'reading' | 'working' | 'read'; label: string } | null>
  /** Fires when anything above changes, so the element redraws. */
  subscribe?(fn: () => void): () => void
}

export interface CommentThreadElement extends HTMLElement {
  port?: CommentPort
  /** What the threads are on: a document, a node in it, a passage. */
  target?: Record<string, string>
  /** Show resolved threads too. Off by default (3e). */
  showResolved?: boolean
  /** What this app's thread can do (RFC 0324 3h). */
  options?: CommentThreadOptions
}

/**
 * What an app asks of the thread it imports (RFC 0324 3h). The app chooses
 * what the thread can do; it never chooses what a comment looks like.
 */
export interface CommentThreadOptions {
  /** Show a box to start a thread when there is none. `'always'` shows it
   *  under the threads too, so a second thread can start where one exists. */
  compose?: boolean | 'always'
  /** Offer @mentions in every box. Names come from `port.candidates`. */
  mentions?: boolean
  /** Beside Reply under each comment. Resolve by default. */
  actions?: Array<'resolve' | 'delete'>
  /** A thread can fold to one line. Fires `mod-comment-fold`. */
  foldable?: boolean
  /** Threads that start folded, by root id: the app remembers folds. */
  folded?: string[]
  /** A short mark beside a comment's time. */
  badge?(comment: CommentElementComment): CommentElementBadge | null
  /** The empty box's placeholder. */
  placeholder?: string
}

/** A count on a passage or a shape (RFC 0324 3b): how many exchanges are
 *  open there, and whose faces are in them. Clicking it fires
 *  `mod-comment-open`, and the app decides what opening means. */
export interface CommentChipElement extends HTMLElement {
  port?: CommentPort
  target?: Record<string, string>
}

export interface InstalledCommentElements {
  CommentRow: CustomElementConstructor
  CommentThread: CustomElementConstructor
  CommentChip: CustomElementConstructor
  /** The elements' stylesheet. The page gets it in <head>; an app that draws
   *  comments inside a shadow root (the Review app's diff) adds it there too,
   *  because a stylesheet cannot cross the shadow boundary. */
  css: string
}

export function installCommentElements(
  target: Window & typeof globalThis = window,
): InstalledCommentElements {
  const existing = (
    target as typeof target & {
      __modCommentElements?: InstalledCommentElements
    }
  ).__modCommentElements
  if (existing) return existing

  const doc = target.document

  const css = `
      mod-comment-row { display:block; color: var(--color-foreground, var(--foreground, currentColor)); font: inherit; }
      .mod-comment-head { display:flex; align-items:center; gap:8px; min-width:0; }
      .mod-comment-avatar { flex:none; width:24px; height:24px; border-radius:50%; overflow:hidden; display:grid; place-items:center; color:#fff; font-size:10px; font-weight:600; line-height:1; }
      .mod-comment-avatar img { width:100%; height:100%; object-fit:cover; display:block; }
      /* A session's face is its mark (RFC 0311): the rounded square every other
         surface draws, a little smaller than a person's photo, centred in the
         same 24px column so names still line up. */
      .mod-comment-head .mod-comment-avatar[data-kind="agent"] { width:18px; height:18px; margin:0 3px; border-radius:22%; }
      .mod-comment-name { min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; font-size:13px; font-weight:600; }
      .mod-comment-tag { flex:none; border:1px solid var(--color-border, var(--border, color-mix(in srgb, currentColor 18%, transparent))); border-radius:5px; padding:0 4px; font-size:10px; color:var(--color-muted-foreground, var(--muted-foreground, currentColor)); }
      .mod-comment-age { flex:none; margin-left:auto; font-size:11px; color:var(--color-muted-foreground, var(--muted-foreground, currentColor)); opacity:.75; }
      .mod-comment-body { margin:6px 0 0; white-space:pre-wrap; overflow-wrap:anywhere; font-size:13px; line-height:1.45; color:var(--color-foreground, var(--foreground, currentColor)); opacity:.88; }
      .mod-comment-source-link { border:0; padding:0; background:none; color:var(--color-link, #60a5fa); text-decoration:underline; text-underline-offset:2px; font:inherit; cursor:pointer; }
      .mod-comment-source-error { color:var(--color-destructive, #ef4444); }
      mod-comment-row { position:relative; }
      .mod-comment-more { flex:none; border:0; padding:0 2px; background:transparent; font:inherit; font-size:13px; letter-spacing:1px; line-height:1; color:var(--color-muted-foreground, var(--muted-foreground, currentColor)); opacity:.7; cursor:pointer; }
      .mod-comment-more:hover { opacity:1; color:var(--color-foreground, var(--foreground, currentColor)); }
      .mod-comment-menu { position:absolute; right:0; top:26px; z-index:6; min-width:140px; padding:4px; border-radius:10px; background:var(--color-popover, var(--popover, #262626)); border:1px solid var(--color-border, var(--border, #3a3a3a)); box-shadow:0 10px 30px rgba(0,0,0,.45); }
      .mod-comment-menu button { display:block; width:100%; border:0; border-radius:7px; padding:5px 8px; background:transparent; color:inherit; font:inherit; font-size:12.5px; text-align:left; cursor:pointer; }
      .mod-comment-menu button:hover { background:color-mix(in srgb, currentColor 10%, transparent); }
      .mod-comment-menu button[data-confirming="true"] { color:#ef4444; }
      mod-comment-thread { display:flex; flex-direction:column; gap:8px; color: var(--color-foreground, var(--foreground, currentColor)); font: inherit; }
      .mod-thread-card { position:relative; border-radius:12px; padding:10px 12px; background:var(--color-muted, var(--muted, color-mix(in srgb, currentColor 7%, transparent))); display:flex; flex-direction:column; gap:10px; }
      .mod-thread-card[data-resolved="true"] { opacity:.55; }
      .mod-thread-line { display:flex; align-items:center; gap:6px; font-size:11px; color:var(--color-muted-foreground, var(--muted-foreground, currentColor)); }
      .mod-thread-line[data-tone="warn"] { color:#eab308; }
      .mod-thread-dot { width:6px; height:6px; border-radius:50%; background:#3b82f6; flex:none; }
      .mod-thread-line[data-tone="warn"] .mod-thread-dot { background:#eab308; }
      .mod-thread-compose { border:1px solid var(--color-border, var(--border, color-mix(in srgb, currentColor 18%, transparent))); border-radius:12px; background:var(--color-background, var(--background, transparent)); }
      .mod-thread-compose:focus-within { border-color:#3b82f6; box-shadow:0 0 0 3px rgba(59,130,246,.18); }
      .mod-thread-input { display:block; width:100%; box-sizing:border-box; min-height:34px; max-height:220px; resize:none; overflow-y:hidden; border:0; padding:9px 12px 4px; background:transparent; color:inherit; font:inherit; font-size:13px; line-height:1.45; outline:none; }
      .mod-thread-tools { display:flex; align-items:center; gap:12px; margin:4px 8px 0; padding:6px 0 7px 4px; border-top:1px solid var(--color-border, var(--border, color-mix(in srgb, currentColor 12%, transparent))); }
      .mod-thread-tools-space { flex:1; }
      .mod-thread-at { border:0; padding:0 2px; background:transparent; font:inherit; font-size:15px; line-height:1; color:var(--color-muted-foreground, var(--muted-foreground, currentColor)); cursor:pointer; }
      .mod-thread-at:hover { color:var(--color-foreground, var(--foreground, currentColor)); }
      .mod-thread-send { border:0; border-radius:8px; padding:3px 12px; background:var(--color-foreground, var(--foreground, #e6e6e6)); color:var(--color-background, var(--background, #1b1b1b)); font:inherit; font-size:12px; font-weight:600; cursor:pointer; }
      .mod-thread-send:disabled { opacity:.4; cursor:default; }
      .mod-thread-resolve { border:0; padding:0; background:transparent; font:inherit; font-size:12px; color:var(--color-muted-foreground, var(--muted-foreground, currentColor)); cursor:pointer; }
      .mod-thread-resolve:hover { color:var(--color-foreground, var(--foreground, currentColor)); }
      .mod-thread-fold-toggle { position:absolute; top:8px; right:10px; border:0; padding:2px 4px; background:transparent; font:inherit; font-size:11px; line-height:1; color:var(--color-muted-foreground, var(--muted-foreground, currentColor)); opacity:.6; cursor:pointer; }
      .mod-thread-fold-toggle:hover { opacity:1; }
      .mod-thread-error { font-size:11px; color:#ef4444; }
      .mod-comment-badge { flex:none; font-size:10px; border-radius:5px; padding:0 5px; color:var(--color-muted-foreground, var(--muted-foreground, currentColor)); border:1px solid var(--color-border, var(--border, color-mix(in srgb, currentColor 18%, transparent))); }
      .mod-comment-badge[data-tone="warn"] { color:#eab308; border-color:rgba(234,179,8,.45); }
      .mod-thread-fold { display:flex; align-items:center; gap:8px; border:0; padding:0; background:transparent; font:inherit; font-size:12px; color:var(--color-muted-foreground, var(--muted-foreground, currentColor)); cursor:pointer; text-align:left; }
      .mod-thread-fold:hover { color:var(--color-foreground, var(--foreground, currentColor)); }
      .mod-thread-picker { position:relative; }
      .mod-thread-options { position:absolute; left:0; top:100%; z-index:5; min-width:200px; margin-top:4px; padding:4px; border-radius:10px; background:var(--color-popover, var(--popover, #262626)); border:1px solid var(--color-border, var(--border, #3a3a3a)); box-shadow:0 10px 30px rgba(0,0,0,.45); }
      .mod-thread-option { display:flex; align-items:center; gap:8px; width:100%; border:0; border-radius:7px; padding:5px 8px; background:transparent; color:inherit; font:inherit; font-size:12.5px; text-align:left; cursor:pointer; }
      .mod-thread-option[data-active="true"], .mod-thread-option:hover { background:color-mix(in srgb, currentColor 10%, transparent); }
      .mod-thread-option-kind { margin-left:auto; font-size:11px; color:var(--color-muted-foreground, var(--muted-foreground, currentColor)); }
      .mod-comment-mention { border-radius:5px; padding:0 4px; color:#60a5fa; background:rgba(59,130,246,.16); }
      mod-comment-chip { display:inline-flex; }
      mod-comment-chip[hidden] { display:none; }
      .mod-chip { display:inline-flex; align-items:center; gap:6px; border:1px solid var(--color-border, var(--border, color-mix(in srgb, currentColor 18%, transparent))); border-radius:999px; padding:2px 8px 2px 3px; background:var(--color-background, var(--background, transparent)); color:inherit; font:inherit; font-size:11px; cursor:pointer; }
      .mod-chip:hover { border-color:#3b82f6; }
      .mod-chip-faces { display:inline-flex; }
      .mod-chip-faces .mod-comment-avatar { width:18px; height:18px; font-size:8px; box-shadow:0 0 0 2px var(--color-background, var(--background, #111)); }
      .mod-chip-faces .mod-comment-avatar + .mod-comment-avatar { margin-left:-6px; }
      .mod-chip-faces .mod-comment-avatar[data-kind="agent"] { border-radius:22%; }
      mod-comment-chip[variant="pin"] .mod-chip { position:relative; border:0; padding:0; border-radius:0; background:transparent; }
      mod-comment-chip[variant="pin"] .mod-chip-faces .mod-comment-avatar { box-sizing:border-box; width:24px; height:24px; font-size:9px; border:2px solid #3b82f6; border-radius:50% 50% 50% 4px; box-shadow:0 4px 12px rgba(0,0,0,.4); }
      mod-comment-chip[variant="pin"] .mod-chip-faces .mod-comment-avatar + .mod-comment-avatar { display:none; }
      mod-comment-chip[variant="pin"] .mod-chip-count { position:absolute; top:-5px; right:-7px; min-width:14px; height:14px; box-sizing:border-box; padding:0 3px; border-radius:7px; background:#3b82f6; color:#fff; font-size:9px; font-weight:600; line-height:14px; text-align:center; }
      mod-comment-chip[variant="pin"] .mod-chip:hover .mod-comment-avatar { border-color:#60a5fa; }
    `
  if (!doc.querySelector('style[data-mod-comment-ui]')) {
    const style = doc.createElement('style')
    style.dataset.modCommentUi = ''
    style.textContent = css
    doc.head.appendChild(style)
  }

  /** Relative time, coarse on purpose: a comment wants "2h", not a date. */
  function ago(then: number, now: number): string {
    const secs = Math.max(0, Math.round((now - then) / 1000))
    if (secs < 60) return 'just now'
    const mins = Math.round(secs / 60)
    if (mins < 60) return `${mins}m`
    const hours = Math.round(mins / 60)
    if (hours < 24) return `${hours}h`
    return `${Math.round(hours / 24)}d`
  }

  /** The body as text and mention pieces. A mention outside the text, or one
   *  overlapping the last, is dropped rather than drawn over the wrong words. */
  function pieces(
    body: string,
    mentions: Array<{ at: number; len: number }> | undefined,
  ): Array<{ text: string; mention: boolean }> {
    const sorted = (mentions ?? [])
      .filter((m) => m.at >= 0 && m.len > 0 && m.at + m.len <= body.length)
      .sort((a, b) => a.at - b.at)
    const out: Array<{ text: string; mention: boolean }> = []
    let pos = 0
    for (const m of sorted) {
      if (m.at < pos) continue
      if (m.at > pos) out.push({ text: body.slice(pos, m.at), mention: false })
      out.push({ text: body.slice(m.at, m.at + m.len), mention: true })
      pos = m.at + m.len
    }
    if (pos < body.length) out.push({ text: body.slice(pos), mention: false })
    return out
  }

  class ModCommentRow extends target.HTMLElement {
    private declare _comment?: CommentElementComment
    private declare _author?: CommentElementAuthor
    private declare _now?: number
    private declare _actions?: CommentElementAction[]
    private declare _badge?: CommentElementBadge | null

    get badge() {
      return this._badge
    }
    set badge(v: CommentElementBadge | null | undefined) {
      this._badge = v
      this.render()
    }

    get comment() {
      return this._comment
    }
    set comment(v: CommentElementComment | undefined) {
      this._comment = v
      this.render()
    }
    get author() {
      return this._author
    }
    set author(v: CommentElementAuthor | undefined) {
      this._author = v
      this.render()
    }
    get now() {
      return this._now
    }
    set now(v: number | undefined) {
      this._now = v
      this.render()
    }

    get actions() {
      return this._actions
    }
    set actions(v: CommentElementAction[] | undefined) {
      this._actions = v
      this.render()
    }

    connectedCallback() {
      this.render()
    }

    render() {
      const c = this._comment
      if (!c) {
        this.replaceChildren()
        return
      }
      const a: CommentElementAuthor = this._author ?? {
        label: 'unknown',
        kind: 'person',
        initials: '?',
        tint: '#6b7280',
      }

      const head = doc.createElement('div')
      head.className = 'mod-comment-head'

      const avatar = doc.createElement('span')
      avatar.className = 'mod-comment-avatar'
      avatar.dataset.kind = a.kind
      if (a.avatarUrl) {
        const img = doc.createElement('img')
        img.src = a.avatarUrl
        img.alt = ''
        img.referrerPolicy = 'no-referrer'
        avatar.appendChild(img)
      } else {
        avatar.style.background = a.tint
        avatar.textContent = a.initials || '?'
      }
      head.appendChild(avatar)

      const name = doc.createElement('span')
      name.className = 'mod-comment-name'
      name.textContent = a.label
      name.title = a.label
      head.appendChild(name)

      if (a.kind === 'agent') {
        const tag = doc.createElement('span')
        tag.className = 'mod-comment-tag'
        tag.textContent = 'session'
        head.appendChild(tag)
      }

      if (this._badge?.text) {
        const badge = doc.createElement('span')
        badge.className = 'mod-comment-badge'
        badge.dataset.tone = this._badge.tone ?? 'muted'
        badge.textContent = this._badge.text
        if (this._badge.title) badge.title = this._badge.title
        head.appendChild(badge)
      }

      const age = doc.createElement('span')
      age.className = 'mod-comment-age'
      age.textContent = ago(c.createdAt, this._now ?? Date.now())
      head.appendChild(age)

      const body = doc.createElement('p')
      body.className = 'mod-comment-body'
      const appendText = (value: string) => {
        // Only Code subjects become links. Build DOM nodes, never HTML from a
        // comment body, and leave every other Markdown spelling as text.
        const sourceLink = /\[([^\]\n]+)\]\((code(?:-source)?:[^\s)]+)\)/g
        let pos = 0
        for (const match of value.matchAll(sourceLink)) {
          const at = match.index ?? 0
          if (at > pos)
            body.appendChild(doc.createTextNode(value.slice(pos, at)))
          const button = doc.createElement('button')
          button.type = 'button'
          button.className = 'mod-comment-source-link'
          button.textContent = match[1] ?? ''
          button.title = 'Open in Code'
          const id = match[2] ?? ''
          button.addEventListener('click', () => {
            const mod = (
              target as typeof target & {
                mod?: { docs?: { open?: (id: string) => Promise<unknown> } }
              }
            ).mod
            if (mod?.docs?.open)
              void mod.docs.open(id).catch((err: unknown) => {
                const failure = doc.createElement('span')
                failure.className = 'mod-comment-source-error'
                failure.textContent = ` (${err instanceof Error ? err.message : 'Could not open file'})`
                const previous = button.nextElementSibling
                if (previous?.classList.contains('mod-comment-source-error'))
                  previous.remove()
                button.after(failure)
              })
            else
              this.dispatchEvent(
                new target.CustomEvent('mod-comment-source-open', {
                  bubbles: true,
                  detail: { id },
                }),
              )
          })
          body.appendChild(button)
          pos = at + match[0].length
        }
        if (pos < value.length)
          body.appendChild(doc.createTextNode(value.slice(pos)))
      }
      for (const piece of pieces(c.body, c.mentions)) {
        if (piece.mention) {
          const pill = doc.createElement('span')
          pill.className = 'mod-comment-mention'
          pill.textContent = piece.text
          body.appendChild(pill)
        } else {
          appendText(piece.text)
        }
      }

      // Actions sit behind ••• (RFC 0324 design): a comment reads as what was
      // said, not as a row of verbs under every line.
      if (this._actions && this._actions.length > 0) {
        const actions = this._actions
        const more = doc.createElement('button')
        more.type = 'button'
        more.className = 'mod-comment-more'
        more.title = 'More'
        more.textContent = '•••'
        let menu: HTMLElement | null = null
        const close = () => {
          menu?.remove()
          menu = null
          doc.removeEventListener('click', close)
        }
        more.addEventListener('click', (e) => {
          e.stopPropagation()
          if (menu) return close()
          menu = doc.createElement('div')
          menu.className = 'mod-comment-menu'
          for (const a of actions) {
            const b = doc.createElement('button')
            b.type = 'button'
            b.textContent = a.label
            b.addEventListener('click', (ev) => {
              ev.stopPropagation()
              if (a.confirm && b.dataset.confirming !== 'true') {
                b.dataset.confirming = 'true'
                b.textContent = a.confirm
                return
              }
              close()
              a.run()
            })
            menu.appendChild(b)
          }
          this.appendChild(menu)
          doc.addEventListener('click', close)
        })
        head.appendChild(more)
      }
      this.replaceChildren(head, body)
    }
  }

  if (!target.customElements.get('mod-comment-row'))
    target.customElements.define('mod-comment-row', ModCommentRow)

  /**
   * One or more exchanges on a target, drawn from a port (RFC 0324 S2b):
   * rows, what the thread is waiting for, a reply box that says who will
   * read the reply, and resolve. The same element in the window and a frame.
   */
  class ModCommentThread extends target.HTMLElement {
    private declare _port?: CommentPort
    private declare _target?: Record<string, string>
    private declare _showResolved: boolean
    private declare _options: CommentThreadOptions
    private declare _folded: Set<string>
    private declare _unsubscribe?: () => void
    private declare _drafts: Map<string, string>
    /** The mentions picked in each box, kept beside its draft: a redraw
     *  rebuilds the box, and a pick held only in the old one was lost. */
    private declare _picked: Map<string, CommentElementCandidate[]>
    private declare _seq: number

    constructor() {
      super()
      this._showResolved = false
      this._options = {}
      this._folded = new Set()
      this._drafts = new Map()
      this._picked = new Map()
      this._seq = 0
    }

    get port() {
      return this._port
    }
    set port(v: CommentPort | undefined) {
      this._port = v
      this.subscribe()
      void this.refresh()
    }
    get target() {
      return this._target
    }
    set target(v: Record<string, string> | undefined) {
      this._target = v
      void this.refresh()
    }
    get showResolved() {
      return this._showResolved
    }
    set showResolved(v: boolean) {
      this._showResolved = Boolean(v)
      void this.refresh()
    }
    get options() {
      return this._options
    }
    set options(v: CommentThreadOptions | undefined) {
      this._options = v ?? {}
      this._folded = new Set(this._options.folded ?? [])
      void this.refresh()
    }

    connectedCallback() {
      this.subscribe()
      void this.refresh()
    }
    disconnectedCallback() {
      this._unsubscribe?.()
      this._unsubscribe = undefined
    }

    private subscribe() {
      this._unsubscribe?.()
      this._unsubscribe = this._port?.subscribe?.(() => void this.refresh())
    }

    async refresh() {
      const port = this._port
      const where = this._target
      if (!port || !where || !this.isConnected) return
      // Only the latest refresh draws, so a slow answer never paints over a
      // newer one.
      const seq = ++this._seq
      let all: CommentElementThread[]
      try {
        all = await port.threads(where)
      } catch (err) {
        // A read that fails is said, not swallowed: the app hears it and
        // decides whether to show it, and nothing below draws an empty box
        // under an error.
        if (seq !== this._seq) return
        this.replaceChildren()
        this.dispatchEvent(
          new CustomEvent('mod-comment-error', {
            bubbles: true,
            detail: { error: err instanceof Error ? err.message : String(err) },
          }),
        )
        return
      }
      const shown = this._showResolved
        ? all
        : all.filter((t) => !t.root.resolved)
      const extras = await Promise.all(
        shown.map(async (t) => ({
          pending: port.pending ? await port.pending(t.root.id) : null,
          reader: port.reader ? await port.reader(where, t.root.id) : null,
        })),
      )
      // 3h: an empty target the app wants to comment on gets a box.
      const composing =
        (this._options.compose === 'always' ||
          (shown.length === 0 && this._options.compose)) &&
        port.create
      const composeReader =
        composing && port.reader ? await port.reader(where, '') : null
      if (seq !== this._seq) return
      const cards = shown.map((t, i) =>
        this.card(t, extras[i] ?? { pending: null, reader: null }),
      )
      if (composing) cards.push(this.composeCard(composeReader))
      this.replaceChildren(...cards)
    }

    /**
     * A reply or compose box: the textarea, @mentions when asked for (3h), and
     * Enter to send. Cleared only on success, so a failed send keeps the words.
     */
    private box(
      key: string,
      placeholder: string,
      send: (
        body: string,
        mentions: CommentElementMention[],
      ) => Promise<unknown>,
      error: HTMLElement,
      leading?: HTMLElement,
    ): HTMLElement {
      // One rounded box: the text, then a toolbar with @ and Send (RFC 0324
      // design, "Comment box"). The picker hangs off the box.
      const wrap = doc.createElement('div')
      wrap.className = 'mod-thread-picker mod-thread-compose'
      const input = doc.createElement('textarea')
      input.className = 'mod-thread-input'
      input.rows = 1
      input.placeholder = placeholder
      input.value = this._drafts.get(key) ?? ''
      wrap.appendChild(input)
      // The box grows with what you write, up to a cap, and scrolls only past
      // it: two fixed lines hid everything after the second behind a scroll.
      const grow = () => {
        input.style.height = 'auto'
        const full = input.scrollHeight
        input.style.height = `${Math.min(full, 220)}px`
        input.style.overflowY = full > 220 ? 'auto' : 'hidden'
      }
      input.addEventListener('input', grow)
      queueMicrotask(grow)
      const tools = doc.createElement('div')
      tools.className = 'mod-thread-tools'
      if (leading) tools.appendChild(leading)
      const space = doc.createElement('span')
      space.className = 'mod-thread-tools-space'
      tools.appendChild(space)
      if (this._options.mentions && this._port?.candidates) {
        const at = doc.createElement('button')
        at.type = 'button'
        at.className = 'mod-thread-at'
        at.title = 'Mention a session'
        at.textContent = '@'
        at.addEventListener('click', () => {
          const v = input.value
          input.value = v && !v.endsWith(' ') ? `${v} @` : `${v}@`
          input.focus()
          input.selectionStart = input.selectionEnd = input.value.length
          input.dispatchEvent(new Event('input'))
        })
        tools.appendChild(at)
      }
      const sendButton = doc.createElement('button')
      sendButton.type = 'button'
      sendButton.className = 'mod-thread-send'
      sendButton.textContent = 'Send'
      tools.appendChild(sendButton)
      wrap.appendChild(tools)
      const chosen = this._picked.get(key) ?? []
      this._picked.set(key, chosen)
      let menu: HTMLElement | null = null
      let options: CommentElementCandidate[] = []
      let active = 0
      const closeMenu = () => {
        menu?.remove()
        menu = null
        options = []
      }
      const pick = (c: CommentElementCandidate) => {
        const caret = input.selectionStart ?? input.value.length
        const before = input.value.slice(0, caret).replace(/@[^\s@]*$/, '')
        input.value = `${before}@${c.label} ${input.value.slice(caret)}`
        chosen.push(c)
        this._drafts.set(key, input.value)
        closeMenu()
        input.focus()
      }
      const showMenu = (list: CommentElementCandidate[]) => {
        closeMenu()
        if (list.length === 0) return
        options = list.slice(0, 6)
        active = 0
        menu = doc.createElement('div')
        menu.className = 'mod-thread-options'
        options.forEach((c, i) => {
          const b = doc.createElement('button')
          b.type = 'button'
          b.className = 'mod-thread-option'
          b.dataset.active = String(i === active)
          b.append(c.label)
          const kind = doc.createElement('span')
          kind.className = 'mod-thread-option-kind'
          kind.textContent = c.kind
          b.appendChild(kind)
          b.addEventListener('mousedown', (e) => {
            e.preventDefault()
            pick(c)
          })
          menu?.appendChild(b)
        })
        wrap.appendChild(menu)
      }
      input.addEventListener('input', () => {
        this._drafts.set(key, input.value)
        if (!this._options.mentions || !this._port?.candidates) return
        const caret = input.selectionStart ?? input.value.length
        const m = /@([^\s@]*)$/.exec(input.value.slice(0, caret))
        if (!m) return closeMenu()
        const prefix = m[1] ?? ''
        void this._port.candidates(prefix).then((list) => {
          // Stale answers for an earlier prefix are dropped.
          const now = /@([^\s@]*)$/.exec(
            input.value.slice(0, input.selectionStart ?? input.value.length),
          )
          if (now && now[1] === prefix) showMenu(list)
        })
      })
      input.addEventListener('blur', () => setTimeout(closeMenu, 100))
      input.addEventListener('keydown', (e) => {
        if (menu && options.length > 0) {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault()
            active =
              (active + (e.key === 'ArrowDown' ? 1 : -1) + options.length) %
              options.length
            menu
              .querySelectorAll<HTMLElement>('.mod-thread-option')
              .forEach((el, i) => {
                el.dataset.active = String(i === active)
              })
            return
          }
          if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault()
            const c = options[active]
            if (c) pick(c)
            return
          }
          if (e.key === 'Escape') {
            e.preventDefault()
            closeMenu()
            return
          }
        }
        if (e.key === 'Escape' && key === '__compose__') {
          this.dispatchEvent(
            new CustomEvent('mod-comment-cancel', { bubbles: true }),
          )
          return
        }
        if (e.key !== 'Enter' || e.shiftKey) return
        e.preventDefault()
        submit()
      })
      const submit = () => {
        const body = input.value.trim()
        if (!body || input.disabled) return
        // A mention counts only if its "@label" is still in the text, and
        // where it is NOW, not where it was when it was picked.
        const mentions: CommentElementMention[] = []
        for (const c of chosen) {
          const at = body.indexOf(`@${c.label}`)
          if (at >= 0) mentions.push({ ...c, at, len: c.label.length + 1 })
        }
        input.disabled = true
        sendButton.disabled = true
        send(body, mentions).then(
          () => {
            this._drafts.delete(key)
            this._picked.delete(key)
            void this.refresh()
          },
          (err: unknown) => {
            input.disabled = false
            sendButton.disabled = false
            error.textContent = `Not sent: ${err instanceof Error ? err.message : String(err)}`
          },
        )
      }
      sendButton.addEventListener('click', submit)
      return wrap
    }

    private composeCard(
      reader: { text: string; tone: string } | null,
    ): HTMLElement {
      const card = doc.createElement('div')
      card.className = 'mod-thread-card'
      card.dataset.compose = 'true'
      const error = doc.createElement('div')
      error.className = 'mod-thread-error'
      const where = this._target ?? {}
      const box = this.box(
        '__compose__',
        this._options.placeholder ?? 'Comment…',
        (body, mentions) =>
          this._port?.create
            ? this._port.create(where, body, mentions)
            : Promise.reject(new Error('this app cannot start a thread')),
        error,
      )
      card.appendChild(box)
      if (reader) card.appendChild(this.line(reader.text, reader.tone))
      card.appendChild(error)
      queueMicrotask(() => box.querySelector('textarea')?.focus())
      return card
    }

    private line(text: string, tone: string): HTMLElement {
      const line = doc.createElement('div')
      line.className = 'mod-thread-line'
      line.dataset.tone = tone
      const dot = doc.createElement('span')
      dot.className = 'mod-thread-dot'
      line.append(dot, text)
      return line
    }

    private setFolded(rootId: string, folded: boolean) {
      if (folded) this._folded.add(rootId)
      else this._folded.delete(rootId)
      this.dispatchEvent(
        new CustomEvent('mod-comment-fold', {
          bubbles: true,
          detail: { rootId, folded },
        }),
      )
      void this.refresh()
    }

    private card(
      t: CommentElementThread,
      extra: {
        pending: { state: string; label: string } | null
        reader: { text: string; tone: string } | null
      },
    ): HTMLElement {
      const card = doc.createElement('div')
      card.className = 'mod-thread-card'
      card.dataset.resolved = String(Boolean(t.root.resolved))
      const all = [t.root, ...t.replies]
      const opts = this._options

      // 3h: folded to one line saying who spoke first and how many comments.
      if (opts.foldable && this._folded.has(t.root.id)) {
        const open = doc.createElement('button')
        open.type = 'button'
        open.className = 'mod-thread-fold'
        open.textContent = `▸ ${t.root.authorInfo.label} · ${all.length} comment${all.length === 1 ? '' : 's'}`
        open.addEventListener('click', () => this.setFolded(t.root.id, false))
        card.appendChild(open)
        return card
      }
      if (opts.foldable) {
        // A quiet corner control, not a line of its own above the comment.
        const fold = doc.createElement('button')
        fold.type = 'button'
        fold.className = 'mod-thread-fold-toggle'
        fold.title = 'Fold this thread'
        fold.textContent = '▾'
        fold.addEventListener('click', () => this.setFolded(t.root.id, true))
        card.appendChild(fold)
      }

      const now = Date.now()
      const canDelete = opts.actions?.includes('delete') && this._port?.remove
      for (const c of all) {
        const row = doc.createElement('mod-comment-row') as HTMLElement & {
          comment?: unknown
          author?: unknown
          now?: number
          badge?: unknown
          actions?: CommentElementAction[]
        }
        row.comment = c
        row.author = c.authorInfo
        row.now = now
        if (opts.badge) row.badge = opts.badge(c)
        if (canDelete) {
          row.actions = [
            {
              // Two clicks, because delete has no undo.
              label: 'Delete',
              confirm: 'Delete? Yes',
              run: () => {
                void this._port?.remove?.(c.id).then(() => this.refresh())
              },
            },
          ]
        }
        card.appendChild(row)
      }
      if (extra.pending) {
        const line = doc.createElement('div')
        line.className = 'mod-thread-line'
        if (extra.pending.state === 'read') {
          line.textContent = `✓ Read by ${extra.pending.label}`
        } else {
          const dot = doc.createElement('span')
          dot.className = 'mod-thread-dot'
          line.append(dot, `${extra.pending.label} is ${extra.pending.state}`)
        }
        card.appendChild(line)
      }
      const error = doc.createElement('div')
      error.className = 'mod-thread-error'
      // Resolve unless the app asked for actions without it (3h). It sits in
      // the box's toolbar, on the left, away from Send.
      let resolve: HTMLElement | undefined
      if (!opts.actions || opts.actions.includes('resolve')) {
        const b = doc.createElement('button')
        b.type = 'button'
        b.className = 'mod-thread-resolve'
        b.textContent = t.root.resolved ? 'Reopen' : 'Resolve'
        b.addEventListener('click', () => {
          void this._port
            ?.resolve(t.root.id, !t.root.resolved)
            .then(() => this.refresh())
        })
        resolve = b
      }
      card.appendChild(
        this.box(
          t.root.id,
          'Reply…',
          (body, mentions) =>
            this._port
              ? this._port.reply(t.root.id, body, mentions)
              : Promise.reject(new Error('no port')),
          error,
          resolve,
        ),
      )
      if (extra.reader && !t.root.resolved) {
        card.appendChild(this.line(extra.reader.text, extra.reader.tone))
      }
      card.appendChild(error)
      return card
    }
  }

  if (!target.customElements.get('mod-comment-thread'))
    target.customElements.define('mod-comment-thread', ModCommentThread)

  /**
   * The count on a passage or a shape (RFC 0324 3b, S2). Open exchanges only,
   * because a chip is a thing to act on, and up to three faces of who wrote
   * in them, first writer first. Hidden when there is nothing open, so an app
   * can put one on every shape without a row of zeros.
   */
  class ModCommentChip extends target.HTMLElement {
    private declare _port?: CommentPort
    private declare _target?: Record<string, string>
    private declare _unsubscribe?: () => void
    private declare _seq: number

    constructor() {
      super()
      this._seq = 0
    }

    get port() {
      return this._port
    }
    set port(v: CommentPort | undefined) {
      this._port = v
      this.subscribe()
      void this.refresh()
    }
    get target() {
      return this._target
    }
    set target(v: Record<string, string> | undefined) {
      this._target = v
      void this.refresh()
    }

    connectedCallback() {
      this.subscribe()
      void this.refresh()
    }
    disconnectedCallback() {
      this._unsubscribe?.()
      this._unsubscribe = undefined
    }

    private subscribe() {
      this._unsubscribe?.()
      this._unsubscribe = this._port?.subscribe?.(() => void this.refresh())
    }

    async refresh() {
      const port = this._port
      const where = this._target
      if (!port || !where || !this.isConnected) return
      const seq = ++this._seq
      const open = (await port.threads(where)).filter((t) => !t.root.resolved)
      if (seq !== this._seq) return
      this.hidden = open.length === 0
      if (open.length === 0) {
        this.replaceChildren()
        return
      }
      const faces: CommentElementAuthor[] = []
      const seen = new Set<string>()
      for (const t of open) {
        for (const c of [t.root, ...t.replies]) {
          const key = `${c.authorInfo.kind}:${c.authorInfo.label}`
          if (seen.has(key) || faces.length >= 3) continue
          seen.add(key)
          faces.push(c.authorInfo)
        }
      }
      const button = doc.createElement('button')
      button.type = 'button'
      button.className = 'mod-chip'
      button.title = `${open.length} open comment${open.length === 1 ? '' : 's'}`
      const stack = doc.createElement('span')
      stack.className = 'mod-chip-faces'
      for (const a of faces) {
        const face = doc.createElement('span')
        face.className = 'mod-comment-avatar'
        face.dataset.kind = a.kind
        if (a.avatarUrl) {
          const img = doc.createElement('img')
          img.src = a.avatarUrl
          img.alt = ''
          img.referrerPolicy = 'no-referrer'
          face.appendChild(img)
        } else {
          face.style.background = a.tint
          face.textContent = a.initials || '?'
        }
        stack.appendChild(face)
      }
      // A pin is the first writer's face on the thing, like a map pin; the
      // count shows only when there is more than one thread under it.
      if (this.getAttribute('variant') === 'pin') {
        button.append(stack)
        if (open.length > 1) {
          const count = doc.createElement('span')
          count.className = 'mod-chip-count'
          count.textContent = String(open.length)
          button.append(count)
        }
      } else {
        button.append(stack, String(open.length))
      }
      button.addEventListener('click', (e) => {
        e.stopPropagation()
        this.dispatchEvent(
          new CustomEvent('mod-comment-open', {
            bubbles: true,
            detail: { target: where, threadIds: open.map((t) => t.root.id) },
          }),
        )
      })
      this.replaceChildren(button)
    }
  }

  if (!target.customElements.get('mod-comment-chip'))
    target.customElements.define('mod-comment-chip', ModCommentChip)

  const installed: InstalledCommentElements = {
    CommentRow: target.customElements.get(
      'mod-comment-row',
    ) as CustomElementConstructor,
    CommentThread: target.customElements.get(
      'mod-comment-thread',
    ) as CustomElementConstructor,
    CommentChip: target.customElements.get(
      'mod-comment-chip',
    ) as CustomElementConstructor,
    css,
  }
  ;(
    target as typeof target & {
      __modCommentElements?: InstalledCommentElements
    }
  ).__modCommentElements = installed
  return installed
}

export function ensureCommentElements(): InstalledCommentElements {
  return installCommentElements(window)
}
